import { PencilSquare as Edit, Trash } from '@medusajs/icons';
import {
  clx,
  Heading,
  StatusBadge,
  Text,
  toast,
  usePrompt,
  useToggleState,
} from '@medusajs/ui';
import {
  type CreateAddressInput,
  type CustomerAddress,
  type UpdateAddressInput,
  useDeleteAddress,
  useUpdateAddress,
} from '@lib/query/mutations/customer';
import Spinner from '@modules/common/icons/spinner';

import { EditAddressModal } from './edit-address-modal';

interface EditAddressProps {
  address: CustomerAddress;
  isActive?: boolean;
}

const EditAddress: React.FC<EditAddressProps> = ({
  address,
  isActive = false,
}) => {
  const { state, open, close: closeModal } = useToggleState(false);

  // Design Decision: Generic error messages without error logging are sufficient.
  // Users see a clear error message; detailed error tracking is handled by
  // backend monitoring and Sentry. Frontend logging is not essential here.
  // @see PR #63 review comment
  const updateAddress = useUpdateAddress({
    onSuccess: () => {
      toast.success('收件地址已更新', { duration: 4000 });
      closeModal();
    },
    onError: () => {
      toast.error('更新地址失敗，請稍後再試', { duration: 4000 });
    },
  });

  const deleteAddress = useDeleteAddress({
    onSuccess: () => {
      toast.success('收件地址已刪除', { duration: 4000 });
    },
    onError: () => {
      toast.error('刪除地址失敗，請稍後再試', { duration: 4000 });
    },
  });

  const isDrugAddress = !!address.metadata?.is_licensed_drug_stock_location;

  const handleSubmit = (data: CreateAddressInput | UpdateAddressInput) => {
    // For EditAddress, data will always have address_id since we pass address.id in the modal
    updateAddress.mutate(data as UpdateAddressInput);
  };

  const deletePrompt = usePrompt();
  const askRemoveAddress = async () => {
    const userHasConfirmed = await deletePrompt({
      title: '確認刪除？',
      description: '您確定要將此收貨地址刪除嗎？',
      cancelText: '取消',
      confirmText: '確認',
    });
    if (userHasConfirmed) {
      deleteAddress.mutate({ address_id: address.id });
    }
  };

  return (
    <>
      <div
        className={clx(
          'rounded-rounded flex h-full min-h-[182px] w-full flex-col justify-between border p-5 transition-colors',
          {
            'border-gray-900': isActive,
          }
        )}
        data-testid="address-container"
      >
        <div className="flex flex-col">
          <Heading
            className="text-sm font-semibold leading-6 flex text-left"
            data-testid="address-name"
          >
            <div>
              <span>{address.company}</span>
              {address?.is_default_shipping && (
                <span className="text-blue-30 ml-1">預設地址</span>
              )}
            </div>
            {isDrugAddress && (
              <StatusBadge color="orange" className="ml-auto">
                藥品地址
              </StatusBadge>
            )}
          </Heading>

          <Text
            className="text-grey-60 mt-2 flex flex-col text-left text-sm"
            data-testid="address-company"
          >
            {address.last_name} {address.phone}
          </Text>

          <Text className="text-grey-60 mt-2 flex flex-col text-left text-sm">
            <span data-testid="address-address">
              {address?.postal_code?.split(' ')?.[0] || ''} {address.city}
              {address?.postal_code?.split(' ')?.[1] || ''}
              {address.address_1}
            </span>
          </Text>
        </div>
        <div className="flex items-center gap-x-4">
          <button
            className="text-xs font-normal leading-5 text-ui-fg-base flex items-center gap-x-2"
            onClick={open}
            data-testid="address-edit-button"
          >
            <Edit />
          </button>
          {!isDrugAddress && (
            <button
              className="text-xs font-normal leading-5 text-ui-fg-base flex items-center gap-x-2"
              onClick={askRemoveAddress}
              data-testid="address-delete-button"
            >
              {deleteAddress.isPending ? <Spinner /> : <Trash />}
            </button>
          )}
        </div>
      </div>

      <EditAddressModal
        address={address}
        modalState={state}
        closeModal={closeModal}
        onSubmit={handleSubmit}
        isPending={updateAddress.isPending}
        error={updateAddress.isError ? '更新地址失敗' : null}
        dataTestId="edit-address-modal"
        title="編輯收件地址"
      />
    </>
  );
};

export default EditAddress;
